import { legacy } from '@/shared/api/client';
import type { ApiChannel, ApiEnvelope } from './types';
import { unwrap } from './client';

/**
 * Rotas do master no fixe-api (`/v1/marketing/:idPassbook/*`), via o proxy
 * autenticado do BFF: cota dos dois canais, configuração da loja
 * (consentimento pelo regulamento) e o ajuste manual de créditos.
 */

/** `MktApi.Capacity` (services/marketingQuota.js `capacity`). `null` = sem teto naquele nível. */
export interface AdminCapacity {
  channel: ApiChannel;
  provider: string;
  planName: string;
  cycleLimit: number | null;
  sent: number;
  available: number | null;
  providerBalance?: number | null;
  renewsOn: string;
  companyLimit: number | null;
  companySent: number;
  campaignLimit: number | null;
  canApprove: number | null;
}

export interface AdminChannelSettings {
  provider: string;
  account: string;
  source: 'passbook' | 'default';
  configured: boolean;
  companyLimit: number | null;
  campaignLimit: number | null;
  consentByTerms: boolean;
}

export interface AdminMarketingSettings {
  idPassbook: string;
  email: AdminChannelSettings;
  sms: AdminChannelSettings;
}

export interface QuotaAdjustment {
  id: string;
  channel: ApiChannel;
  /** Negativo = retirada. */
  slots: number;
  message: string;
  createdAt: string;
  createdBy?: string;
}

const base = (idPassbook: string) => `/marketing/${encodeURIComponent(idPassbook)}`;

export const marketingAdminKeys = {
  all: (idPassbook: string) => ['marketing-admin', idPassbook] as const,
  quota: (idPassbook: string) => ['marketing-admin', idPassbook, 'quota'] as const,
  settings: (idPassbook: string) => ['marketing-admin', idPassbook, 'settings'] as const,
  adjustments: (idPassbook: string) => ['marketing-admin', idPassbook, 'adjustments'] as const,
};

export async function fetchAdminQuota(idPassbook: string): Promise<{ email: AdminCapacity | null; sms: AdminCapacity | null }> {
  const data = unwrap(await legacy.get<ApiEnvelope<{ email?: AdminCapacity | null; sms?: AdminCapacity | null }>>(`${base(idPassbook)}/quota`));
  return { email: data?.email ?? null, sms: data?.sms ?? null };
}

export async function fetchAdminSettings(idPassbook: string): Promise<AdminMarketingSettings> {
  return unwrap(await legacy.get<ApiEnvelope<AdminMarketingSettings>>(`${base(idPassbook)}/settings`));
}

/** Grava só o consentimento do canal: o provedor e os limites da loja ficam como estão. */
export async function saveConsentByTerms(idPassbook: string, channel: ApiChannel, consentByTerms: boolean): Promise<AdminMarketingSettings> {
  return unwrap(
    await legacy.put<ApiEnvelope<AdminMarketingSettings>>(`${base(idPassbook)}/settings`, { [channel]: { consentByTerms } }),
  );
}

export async function adjustQuota(
  idPassbook: string,
  input: { channel: ApiChannel; slots: number; reason: string },
): Promise<{ adjustment: QuotaAdjustment; capacity: AdminCapacity | null }> {
  return unwrap(
    await legacy.post<ApiEnvelope<{ adjustment: QuotaAdjustment; capacity: AdminCapacity | null }>>(`${base(idPassbook)}/quota/adjust`, input),
  );
}

export async function fetchQuotaAdjustments(idPassbook: string): Promise<QuotaAdjustment[]> {
  const rows = unwrap(await legacy.get<ApiEnvelope<QuotaAdjustment[]>>(`${base(idPassbook)}/quota/adjustments`));
  return Array.isArray(rows) ? rows : [];
}
